/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import {
  loadActivityFieldsTypes,
  loadPartnerActivityFields,
  loadPartnerActivityFieldsSummary,
  savePartnerActivityFields
} from "../../actionCreators";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { getActivityFields, getPartnerActivityFieldsSummary } from "../../selectors";
import ActivityFieldsComponent from "./ActivityFieldsComponent";
import { shape, required, items } from "@totalsoft/pure-validations";
import { useDirtyFieldValidation } from "@totalsoft/pure-validations-react";
import { useDirtyInfo } from "@totalsoft/change-tracking-react";

const ActivityFieldsContainer = ({
  partnerId,
  actions,
  activityFieldsLoaded,
  activityFields,
  summaryLoaded,
  partnerActivityFieldsSummary,
  setIsDirty,
  hasWritePermission
}) => {
  const [localActivityFields, setLocalActivityFieldsList] = useState([]);
  const [activityDomainsCardExpanded, setActivityDomainsCardExpanded] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [totalPercentageValid, setTotalPercentageValid] = useState(true);

  const [validDefaultActivitySelection, setValidDefaultActivitySelection] = useState(true);
  const [validActivityFieldMultipleSelection, setValidActivityFieldMultipleSelection] = useState(true);

  const [dirtyInfo, setDirtyInfo] = useDirtyInfo();
  const validatorActivityField = items(
    shape({
      activityFieldId: required
    })
  );
  const [validationActivityField, validateActivityField] = useDirtyFieldValidation(validatorActivityField, dirtyInfo);

  const onActivityDomainsCardExpanded = expanded => {
    if (activityDomainsCardExpanded !== expanded) setActivityDomainsCardExpanded(expanded);
  };

  useEffect(() => {
    if (activityDomainsCardExpanded && !activityFieldsLoaded) {
      actions.loadActivityFieldsTypes();
      if (partnerId !== null) actions.loadPartnerActivityFields(partnerId);
    }
  }, [activityDomainsCardExpanded]);

  useEffect(() => {
    if (partnerId !== null) actions.loadPartnerActivityFieldsSummary(partnerId);
  }, []);

  useEffect(() => {
    if (!summaryLoaded && partnerId !== null) {
      actions.loadPartnerActivityFieldsSummary(partnerId);
    }
  }, [summaryLoaded]);

  useEffect(() => {
    if (activityFieldsLoaded) {
      setLocalActivityFieldsList(activityFields);
    }
  }, [activityFieldsLoaded]);

  const addActivityField = () => {
    setValidDefaultActivitySelection(true);
    setValidActivityFieldMultipleSelection(true);

    let localActivityFieldsList = [...localActivityFields, {}];
    setLocalActivityFieldsList(localActivityFieldsList);
    setSaveDisabled(false);
    setIsDirty(true);
  };

  const onActivityFieldPropertyChange = idx => (prop1, prop2) => (value1, value2) => {
    setDirtyInfo(`${prop1}`);
    setValidDefaultActivitySelection(true);
    setValidActivityFieldMultipleSelection(true);

    const isActivityFieldId = prop1 === "activityFieldId";

    const propertyId = prop1;
    const propertyName = prop2;
    const propertyIdValue = value1;
    const propertyNameValue = isActivityFieldId ? value2.substr(value2.indexOf(" ") + 1) : value2;

    const activityFieldCode = isActivityFieldId ? value2.substr(0, value2.indexOf(" ")) : null;

    setLocalActivityFieldsList(
      Object.assign([], {
        ...localActivityFields,
        [idx]: {
          ...localActivityFields[idx],
          [propertyId]: propertyIdValue,
          ...(propertyName && { [propertyName]: propertyNameValue }),
          ...(activityFieldCode && { activityFieldCode: activityFieldCode }),
          isActive: true
        }
      })
    );
    setSaveDisabled(false);
    setIsDirty(true);
  };

  const deleteActivityField = idx => {
    setValidDefaultActivitySelection(true);
    setValidActivityFieldMultipleSelection(true);
    let newActivityFieldsList = localActivityFields.filter(x => localActivityFields.indexOf(x) !== idx);
    setLocalActivityFieldsList(newActivityFieldsList);
    setSaveDisabled(false);
    setIsDirty(true);
  };

  const isTotalPercentageValid = localActivityFields => (min, max) => {
    const totalPercentage = localActivityFields.reduce((totalPercentage, activityField) => {
      return totalPercentage + Number(activityField.weightPercent);
    }, 0);
    if (min <= totalPercentage && totalPercentage <= max) return true;
    return false;
  };

  const validateLocalActivityFields_defaultActivity = localActivityFields => {
    let counter = 0;
    localActivityFields.map(activityField => {
      if (activityField.defaultActivity === true) {
        counter++;
      }
      return counter;
    });
    return counter;
  };

  const saveActivityFields = (partnerId, localActivityFields) => {
    const totalPercentageValid = isTotalPercentageValid(localActivityFields)(0, 100);
    setTotalPercentageValid(totalPercentageValid);

    if (!hasWritePermission) return;

    if (!validateActivityField(localActivityFields) || !totalPercentageValid) {
      return;
    }
    const counter = validateLocalActivityFields_defaultActivity(localActivityFields);
    if (counter > 1) {
      setValidDefaultActivitySelection(false);
    } else if (localActivityFields.length !== new Set(localActivityFields.map(a => a.activityFieldId)).size) {
      setValidActivityFieldMultipleSelection(false);
    } else {
      setValidDefaultActivitySelection(true);
      actions.savePartnerActivityFields(partnerId, localActivityFields, () => {
        setIsDirty(false);
      });
      setSaveDisabled(true);
    }
  };

  useEffect(() => {
    validateActivityField(localActivityFields, dirtyInfo);
  }, [localActivityFields, dirtyInfo]);

  const loadActivityFieldsTypes = search => {
    if (!search) {
      return Promise.resolve([]);
    }
    return actions
      .loadActivityFieldsTypes(search)
      .then(data => data.map(item => ({ value: item.id, label: item.code + " " + item.name })));
  };

  return (
    <ActivityFieldsComponent
      partnerId={partnerId}
      onActivityDomainsCardExpanded={onActivityDomainsCardExpanded}
      activityDomainsCardExpanded={activityDomainsCardExpanded}
      saveDisabled={saveDisabled}
      addActivityField={addActivityField}
      loadActivityFieldsTypes={loadActivityFieldsTypes}
      onPropertyChange={onActivityFieldPropertyChange}
      activityFields={localActivityFields}
      deleteActivityField={deleteActivityField}
      saveActivityFields={saveActivityFields}
      validDefaultActivitySelection={validDefaultActivitySelection}
      validActivityFieldMultipleSelection={validActivityFieldMultipleSelection}
      validationActivityField={validationActivityField}
      partnerActivityFieldsSummary={partnerActivityFieldsSummary}
      totalPercentageValid={totalPercentageValid}
      hasWritePermission={hasWritePermission}
    />
  );
};

ActivityFieldsContainer.propTypes = {
  actions: PropTypes.object.isRequired,
  partnerId: PropTypes.number,
  activityFields: PropTypes.array,
  activityFieldsLoaded: PropTypes.bool.isRequired,
  activityFieldsLoading: PropTypes.bool.isRequired,
  summaryLoaded: PropTypes.bool,
  partnerActivityFieldsSummary: PropTypes.object,
  setIsDirty: PropTypes.func,
  hasWritePermission: PropTypes.bool.isRequired
};

function mapStateToProps(state, ownProps) {
  const {
    content: activityFields,
    loaded: activityFieldsLoaded,
    loading: activityFieldsLoading
  } = getActivityFields(state, ownProps.partnerId);
  const { partnerActivityFieldsSummary, summaryLoaded } = getPartnerActivityFieldsSummary(state, ownProps.partnerId);

  return {
    activityFields,
    activityFieldsLoaded,
    activityFieldsLoading,
    partnerActivityFieldsSummary,
    summaryLoaded
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadActivityFieldsTypes,
        loadPartnerActivityFields,
        loadPartnerActivityFieldsSummary,
        savePartnerActivityFields
      },
      dispatch
    ),
    dispatch
  };
}
export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(ActivityFieldsContainer));
